import type { AudioClip } from "../scene/schema";
import { audioClipEnd } from "./timeline";

/**
 * Loudness of music clips over time: fades, automatic fade-out at the end of
 * the video and ducking while someone speaks. The editor preview sets these
 * values on the <audio> elements, the export turns the same regions into an
 * ffmpeg volume expression.
 */

/** Music level while someone speaks (about −10 dB). */
export const DUCKING_LEVEL = 0.3;
/** Ramp of the ducking in ms. */
export const DUCKING_RAMP_MS = 250;
/** Music that runs past the end of the video fades out over this time. */
export const END_FADE_MS = 1500;

export interface SpeechRegion {
  start: number;
  end: number;
}

/** Merges spoken words into regions (gaps shorter than `gapMs` are bridged). */
export function speechRegions(
  words: readonly { start: number; end: number }[],
  gapMs = 400,
): SpeechRegion[] {
  const sorted = [...words].sort((a, b) => a.start - b.start);
  const regions: SpeechRegion[] = [];
  for (const word of sorted) {
    const last = regions[regions.length - 1];
    if (last && word.start - last.end <= gapMs) last.end = Math.max(last.end, word.end);
    else regions.push({ start: word.start, end: word.end });
  }
  return regions;
}

/** 0..1: how far the ducking is engaged at `t` (trapezoid with ramps). */
export function duckingAmount(regions: readonly SpeechRegion[], t: number): number {
  let amount = 0;
  for (const region of regions) {
    if (t < region.start - DUCKING_RAMP_MS) break;
    if (t > region.end + DUCKING_RAMP_MS) continue;
    const rise = (t - (region.start - DUCKING_RAMP_MS)) / DUCKING_RAMP_MS;
    const fall = (region.end + DUCKING_RAMP_MS - t) / DUCKING_RAMP_MS;
    amount = Math.max(amount, Math.max(0, Math.min(1, rise, fall)));
  }
  return amount;
}

/** Whether the automatic fade-out at the end of the video applies to a clip. */
export function needsEndFade(clip: AudioClip, videoEnd: number): boolean {
  return audioClipEnd(clip) > videoEnd + 50 && videoEnd - clip.start > END_FADE_MS;
}

/** Volume (0..2) of a music clip at timeline position `t`. */
export function musicGainAt(
  clip: AudioClip,
  t: number,
  videoEnd: number,
  regions: readonly SpeechRegion[],
): number {
  const end = audioClipEnd(clip);
  if (t < clip.start || t >= end) return 0;
  let gain = clip.volume;
  if (clip.fadeIn > 0) gain *= Math.min(1, (t - clip.start) / clip.fadeIn);
  if (clip.fadeOut > 0) gain *= Math.min(1, (end - t) / clip.fadeOut);
  if (needsEndFade(clip, videoEnd)) gain *= Math.max(0, Math.min(1, (videoEnd - t) / END_FADE_MS));
  if (clip.ducking && regions.length > 0) {
    gain *= 1 - (1 - DUCKING_LEVEL) * duckingAmount(regions, t);
  }
  return Math.max(0, gain);
}

/** Fade from/to black of a main track clip: 0..1 at local time `local` (ms). */
export function clipFadeAt(
  clip: { fadeIn: number; fadeOut: number },
  local: number,
  duration: number,
): number {
  let value = 1;
  if (clip.fadeIn > 0) value = Math.min(value, local / clip.fadeIn);
  if (clip.fadeOut > 0) value = Math.min(value, (duration - local) / clip.fadeOut);
  return Math.max(0, Math.min(1, value));
}
